import appState from './@store'
import { createContext } from 'preact'
import { useContext, useEffect, useMemo, useRef } from 'preact/hooks'
import { clearAllBodyScrollLocks } from 'body-scroll-lock'
import { on, off } from 'helpers/dom'
import SearchDropdown from './search-dropdown'
import Icon from 'snippets/icon/icon'

let devtools

if (process.env.NODE_ENV === 'development') {
  devtools = require('preact/devtools')
}

const AppState = createContext()

function SearchBar(props) {
  return (
    <AppState.Provider value={appState()}>
      <App slot={props} />
    </AppState.Provider>
  )
}

function App(props) {
  const state = useContext(AppState)

  const originalQuery = useMemo(() => state.originalQuery.value, [state.originalQuery.value])
  const isOpenDropdown = useMemo(() => state.isOpenDropdown.value, [state.isOpenDropdown.value])

  const searchUrl = useMemo(() => {
    let newUrl = `/search?q=${encodeURIComponent(state.originalQuery.value)}`

    if (state.searchValue.value && state.searchValue.value !== state.originalQuery.value) {
      newUrl = `/search?q=${encodeURIComponent(state.searchValue.value)}&oq=${encodeURIComponent(
        state.originalQuery.value
      )}&spell=1`
    }

    if (
      state.searchValue.value &&
      state.correctedQuery.value &&
      state.correctedQuery.value !== state.searchValue.value
    ) {
      newUrl = `/search?q=${encodeURIComponent(state.correctedQuery.value)}&oq=${encodeURIComponent(
        state.searchValue.value
      )}&spell=1`
    }

    return newUrl
  }, [state.originalQuery.value, state.searchValue.value, state.correctedQuery.value])

  const searchForm = useRef(null)
  const searchResult = useRef(null)

  const onCloseDropdown = () => {
    state.closeDropdown()
    clearAllBodyScrollLocks()
  }

  const search = (e) => {
    const currentValue = e.target.value

    state.setOriginalQuery(currentValue)
    state.setSearchValue(currentValue)
    state.setCorrectedQuery(currentValue)

    if (currentValue.trim().length > 0) {
      state.getSuggestion(currentValue)
    } else {
      state.setIsTrendingActive(true)
    }
  }

  const closeSearch = () => {
    state.setOriginalQuery(null)
    onCloseDropdown()
  }

  const onSearch = (e) => {
    e.preventDefault()
    window.location.href = searchUrl
  }

  const onFocus = (e) => {
    const { value } = e.target

    if (value.trim().length > 0) return

    state.getTrending()
    state.setIsOpenDropdown(true)
    state.setIsTrendingActive(true)
  }

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.target.disabled = true
      e.target.disabled = false
      closeSearch()
    }
  }

  const onHandle = (e) => {
    if (
      searchForm.current &&
      !searchForm.current.contains(e.target) &&
      searchResult.current &&
      !searchResult.current.contains(e.target)
    ) {
      closeSearch()
    }
  }

  useEffect(() => {
    on(
      'mousedown',
      (e) => {
        onHandle(e)
      },
      document
    )

    return () => {
      off(
        'mousedown',
        (e) => {
          onHandle(e)
        },
        document
      )
    }
  }, [])

  useEffect(() => {
    if (isOpenDropdown) {
      document.body.classList.add('search-open')
    } else {
      document.body.classList.remove('search-open')
    }
  }, [isOpenDropdown])

  return (
    <>
      <form
        action={searchUrl}
        className="relative w-full lg:mx-auto lg:max-w-[464px] lg-max:mt-3"
        onSubmit={onSearch}
        ref={searchForm}
      >
        <input
          type="text"
          placeholder="Search our range"
          class="input input-placeholder h-11 w-full rounded border-grey-300 !py-2.5 !pl-3.5 !pr-11 text-grey-700"
          value={originalQuery}
          autocomplete="off"
          onInput={search}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
        />
        <button
          type="submit"
          aria-label="Search"
          className="input__icon-wrapper right-3.5 h-6 -translate-y-1/2"
          disabled={!originalQuery?.length}
        >
          <Icon name="icon-search-2" viewBox="0 0 24 24" className="h-6 w-6 text-grey-700" />
        </button>
      </form>
      {isOpenDropdown && (
        <div ref={searchResult}>
          <SearchDropdown state={state} closeSearch={closeSearch} searchUrl={searchUrl}></SearchDropdown>
        </div>
      )}
    </>
  )
}

export default SearchBar
